import { upperFirst } from "lodash";
import { GroupDocument, LearningLogDocument, LearningLogPublication, PersonalDocument, PersonalPublication, PlanningDocument, ProblemDocument, ProblemPublication, SupportPublication } from "./document-types";
const kDocumentKindPattern = /^[a-z][a-zA-Z0-9]*$/;
/**
 * A camelCase identifier: a lowercase letter followed by letters/digits, with no separators or special
 * characters. This matches the built-in document type strings (e.g. "learningLogPublication") and keeps the
 * value safe as a Firestore document id.
 *
 * Required of a `kind`, which is the registry key, and of a `variant`, which a unit config supplies and
 * which becomes the canonical-pointer slot's label — a Firestore path segment. The variant is the value
 * that has to be checked at runtime; the kinds are all written in code.
 */
export function isValidDocumentKind(value) {
    return kDocumentKindPattern.test(value);
}
const gDocumentKindInfoMap = {};
export function registerDocumentKind(kind, info) {
    if (!isValidDocumentKind(kind)) {
        throw new Error(`Document kind "${kind}" is not a valid identifier ` +
            `(must be camelCase letters/digits, with no separators or special characters)`);
    }
    if (gDocumentKindInfoMap[kind]) {
        throw new Error(`Document kind "${kind}" is already registered`);
    }
    gDocumentKindInfoMap[kind] = Object.assign({ kind }, info);
}
/**
 * Every kind is registered by the application at startup, so a kind name resolves to the same definition
 * in every session and for every document, whichever unit it came from.
 */
export function getDocumentKindInfo(kind) {
    return kind ? gDocumentKindInfoMap[kind] : undefined;
}
/** A kind's full stamp set (its metadataFields plus the `kind` key), or `{}` if the kind is unregistered. */
export function getDocumentKindMetadataFields(kind) {
    const info = getDocumentKindInfo(kind);
    if (!info)
        return {};
    return Object.assign({ kind: info.kind }, info.metadataFields);
}
export function getDocumentOwnerType(kind) {
    var _a, _b;
    return (_b = (_a = getDocumentKindInfo(kind)) === null || _a === void 0 ? void 0 : _a.ownerType) !== null && _b !== void 0 ? _b : "user";
}
/**
 * The owner uid to stamp on a new document of the given kind.
 *
 * Throws for a kind that is not registered, rather than defaulting to the creating user the way
 * getDocumentOwnerType does. Defaulting here would hand a group's or a class's document to whoever
 * happened to create it, and because a canonical slot is addressed by its owner, would file it in that
 * user's slot instead of the shared one — both silently.
 */
export function getDocumentOwner(kind, ctx) {
    const info = getDocumentKindInfo(kind);
    if (!info) {
        throw new Error(`Cannot resolve the owner of unregistered document kind "${kind}"`);
    }
    switch (info.ownerType) {
        case "group": return required(ctx.groupOwnerId, kind, "group");
        case "class": return required(ctx.classOwnerId, kind, "class");
        case "user": return ctx.userId;
        default: return ctx.userId;
    }
}
function required(ownerId, kind, ownerType) {
    if (!ownerId) {
        throw new Error(`Cannot create a ${ownerType}-owned document of kind "${kind}": ` +
            `no ${ownerType} owner id is available`);
    }
    return ownerId;
}
/**
 * The owner fields to stamp on a new document of the given kind, beyond the owner uid getDocumentOwner
 * returns. Only a group owner has one.
 */
export function getDocumentOwnerFields(kind, ctx) {
    if (getDocumentOwnerType(kind) !== "group" || !ctx.groupId)
        return {};
    return { groupId: ctx.groupId };
}
/**
 * The container and curriculum fields to stamp on a document of the given kind, selected by its registered
 * `containerType` — which fixes both, so one lookup answers for both axes.
 */
export function getDocumentLocationFields(kind, ctx) {
    var _a;
    switch ((_a = getDocumentKindInfo(kind)) === null || _a === void 0 ? void 0 : _a.containerType) {
        case "classUnit": return {
            unit: ctx.unit,
            context_id: ctx.context_id,
            // Stated explicitly rather than omitted. A curriculum field written as null means "not about an
            // investigation or problem" (firestore.rules `hasPresentField`), the same convention class-contained
            // documents use for `unit: null`. It is what lets Sort Work query for documents about a unit but not
            // a problem — Firestore cannot match a field that is missing.
            investigation: null,
            problem: null
        };
        case "class": return {
            unit: null,
            context_id: ctx.context_id
        };
        case "offering": return {
            unit: ctx.unit,
            investigation: ctx.investigation,
            problem: ctx.problem,
            context_id: ctx.context_id,
            offeringId: ctx.offeringId
        };
        default: return {
            unit: null,
            context_id: ctx.context_id
        };
    }
}
/**
 * The display title a document's kind supplies, for the kinds whose name is a property of the kind itself
 * rather than of the document. A class-wide document is not one of them: its name is authored per slot and
 * stored on the document at creation, so it is read from the document like any other stored title.
 */
export function getDocumentTitle(document) {
    // Keyed on `type` plus `groupId`, not `kind`: a group document may have no stored `kind` yet (we backfill
    // the kind on open but need the title for the lists of documents before they are opened), so it cannot rely
    // on the lookup above. Requiring `groupId` (not just `type === GroupDocument`) matters because a class-wide
    // document also stores `type: "group"` but carries no `groupId`, so without the `groupId` check it would
    // render as "Group undefined Document" instead of falling through to `undefined`, which callers already
    // handle by reading its stored title.
    //
    // TRANSITIONAL: this reads `type` only because a group document may carry no `kind`. Once
    // scripts/backfill-group-document-axes.ts has stamped `kind` on every group document in every
    // environment, this becomes `document.kind === GroupDocument` and the `groupId` check goes away with it —
    // a class-wide document has its own kind, so it can no longer reach this branch at all.
    if (document.type === GroupDocument && document.groupId)
        return `Group ${document.groupId} Document`;
    return undefined;
}
/**
 * A readable label derived from the kind string alone: "drivingQuestionBoard" → "Driving Question
 * Board". Registry-free by design, so it can name a document no other source names — one created before
 * its title was stored, or one whose kind stores no title at all.
 *
 * It recovers the kind's identity, not the author's wording: a slot titled "Our Big Questions" reads as
 * "Driving Question Board" here.
 */
export function getDocumentKindLabel(kind) {
    if (!kind)
        return undefined;
    return upperFirst(kind.replace(/([A-Z])/g, " $1"));
}
/**
 * The kind shared by every class-wide collaborative document. A unit config declares how many of them there
 * are and what each is called, but not what they are: they all have the same shape, so they are one kind,
 * and a document's `variant` says which of them it is.
 */
export const ClassWideDocument = "classWide";
function registerBuiltInDocumentKinds() {
    // A group document is kept in the offering, like the problem documents beside it; what makes it a group's
    // is its owner, which is also where its stored `groupId` comes from (see getDocumentOwnerFields).
    registerDocumentKind(GroupDocument, {
        metadataFields: { concurrent: true },
        ownerType: "group",
        containerType: "offering"
    });
    // Owned by the class as a whole and kept in the class's copy of the unit, so it is about that unit and
    // nothing narrower. Every class-wide document a unit declares shares this, whatever it is called.
    registerDocumentKind(ClassWideDocument, {
        metadataFields: { concurrent: true },
        ownerType: "class",
        containerType: "classUnit"
    });
    const personalLikeKindInfo = {
        metadataFields: {},
        ownerType: "user",
        containerType: "class"
    };
    registerDocumentKind(PersonalDocument, personalLikeKindInfo);
    registerDocumentKind(LearningLogDocument, personalLikeKindInfo);
    registerDocumentKind(PersonalPublication, personalLikeKindInfo);
    registerDocumentKind(LearningLogPublication, personalLikeKindInfo);
    const problemLikeKindInfo = {
        metadataFields: {},
        ownerType: "user",
        containerType: "offering"
    };
    registerDocumentKind(PlanningDocument, problemLikeKindInfo);
    registerDocumentKind(ProblemDocument, problemLikeKindInfo);
    registerDocumentKind(ProblemPublication, problemLikeKindInfo);
    registerDocumentKind(SupportPublication, problemLikeKindInfo);
}
registerBuiltInDocumentKinds();
// Test-only: reset the registry to just the built-in kinds. The registry is module-global mutable state and
// registerDocumentKind throws on duplicates, so a test that registers a kind must reset before another test
// registers the same kind. (Jest gives each test file its own module registry, so this is only needed within a
// file that registers the same kind in more than one test.)
export function resetDocumentKindRegistryForTests() {
    for (const kind of Object.keys(gDocumentKindInfoMap)) {
        delete gDocumentKindInfoMap[kind];
    }
    registerBuiltInDocumentKinds();
}
//# sourceMappingURL=data:application/json;base64,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